<script>
import { debounce } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { CandidateHaveBack } from '@/shared/constants/feedback/CandidateHaveBack'
import baseMixin from '@/mixins/baseMixin'
import { compileCustomProperties } from '@/helpers/log-helper'
import ErrorAlert from '../general/error-alert.vue'

export default {
  components: {
    ErrorAlert,
  },
  mixins: [baseMixin],
  props: {
    bookingId: {
      type: Number,
      required: true,
    },
    candidate: {
      type: Object,
      required: true,
    },
    feedback: {
      type: Object,
      required: true,
    },
  },
  data: function() {
    return {
      rating: 0,
      loadingUrl: false,
      savingFeedback: false,
      haveBack: CandidateHaveBack.PREFERRED, // Default haveBack value
      CandidateHaveBack,
      commentRules: [(v) => v.length <= 200],
      commentText: '',
      commentTextError: false,
      hasErrors: false,
      error: {},
      dialog: false,
      quickFeedbackSuccess: false,
    }
  },
  computed: {
    ...mapGetters({
      isSubmittingQuickFeedback: 'bookings/isSubmittingQuickFeedback',
    }),
    formValid() {
      return !this.commentTextError && this.rating > 0
    },
    feedbackProvided() {
      return this.feedback.provided || this.quickFeedbackSuccess
    },
  },
  created() {
    this.debouncedTrackFeedbackClick = debounce(this.trackFeedbackClick, 1000)
  },
  methods: {
    ...mapActions({
      requestFeedbackFormUrl: 'bookings/requestFeedbackFormUrl',
      submitQuickFeedback: 'bookings/submitQuickFeedback',
    }),
    async requestExtendedFeedback() {
      this.rating = 0
      this.loadingUrl = true

      try {
        const response = await this.requestFeedbackFormUrl({
          bookingId: this.bookingId,
        })

        window.open(response.data.url, '_blank')
      } finally {
        this.loadingUrl = false
        this.resetFeedback()
      }
    },
    openFeedback() {
      this.dialog = true
      if (!this.isDev) this.debouncedTrackFeedbackClick()
    },
    async saveFeedback() {
      this.clearError()
      this.savingFeedback = true

      const response = await this.submitQuickFeedback({
        bookingId: this.bookingId,
        data: {
          rating: this.parseRating(),
          haveBack: this.haveBack,
          comment: this.commentText,
        },
      })

      if (!response.isSuccess) {
        this.hasErrors = true
        this.error = response.error
      } else {
        this.quickFeedbackSuccess = true
        this.resetFeedback()
      }

      this.savingFeedback = false
    },
    parseRating() {
      let zeroIndexedRating = this.rating - 1

      // Fail safe rating values
      if (zeroIndexedRating < 0) {
        zeroIndexedRating = 0
      } else if (zeroIndexedRating > 4) {
        zeroIndexedRating = 4
      }

      return zeroIndexedRating
    },
    resetFeedback() {
      this.rating = 0
      this.haveBack = CandidateHaveBack.PREFERRED
      this.commentText = ''
      this.clearError()
      this.dialog = false
    },
    clearError() {
      this.error = {}
      this.hasErrors = false
    },
    commentTextErrorUpdate(hasError) {
      this.commentTextError = hasError
    },
    trackFeedbackClick() {
      const customProperties = compileCustomProperties(
        this.$store,
        {
          bookingId: this.bookingId,
          timestamp: new Date().toISOString(),
        },
        true
      )
      this.$appInsights.trackEvent({
        name: 'FeedbackClick',
        properties: customProperties,
      })
    },
  },
}
</script>

<template>
  <div>
    <div v-if="feedbackProvided">
      <v-icon small color="green" class="mr-1">mdi-check</v-icon>
      {{ $t('feedback.feedbackProvidedText') }}
    </div>
    <div v-else>
      <v-btn
        small
        text
        class="ml-n4 provide-feedback-btn"
        @click="openFeedback"
        >{{ $t('feedback.provideFeedbackBtn') }}</v-btn
      >
      <v-dialog v-model="dialog" persistent max-width="600">
        <v-card class="feedback-dialog-content">
          <v-card-title>{{ $t('feedback.quickFeedbackTitle') }}</v-card-title>

          <v-card-text>
            <div class="text-center mb-2">
              <v-rating
                v-model="rating"
                background-color="purple lighten-3"
                color="purple"
                class="quick-feedback-rating"
                hover
                length="5"
              ></v-rating>
              <v-btn
                text
                :loading="loadingUrl"
                :disabled="isSubmittingQuickFeedback"
                @click="requestExtendedFeedback"
                >{{ $t('feedback.extendedFeedbackBtn') }}</v-btn
              >
            </div>
            <v-radio-group
              v-model="haveBack"
              class="preferred-radio-group"
              mandatory
              column
            >
              <template v-slot:label>
                <div>
                  {{
                    $t('feedback.haveBackQuestionText', {
                      ...candidate,
                    })
                  }}
                </div>
              </template>
              <v-radio
                :label="$t('feedback.haveBackPreferredOption')"
                :value="CandidateHaveBack.PREFERRED"
                :class="`preferred-radio-${CandidateHaveBack.PREFERRED}`"
              ></v-radio>
              <v-radio
                :label="$t('feedback.haveBackYesOption')"
                :value="CandidateHaveBack.YES"
                :class="`preferred-radio-${CandidateHaveBack.YES}`"
              ></v-radio>
              <v-radio
                :label="$t('feedback.haveBackNoOption')"
                :value="CandidateHaveBack.NO"
                :class="`preferred-radio-${CandidateHaveBack.NO}`"
              ></v-radio>
            </v-radio-group>
            <v-expand-transition>
              <v-textarea
                v-if="haveBack === CandidateHaveBack.NO"
                v-model="commentText"
                outlined
                :label="$t('feedback.haveBackCommentText')"
                :rules="commentRules"
                class="comment-textarea"
                counter
                @update:error="commentTextErrorUpdate"
              ></v-textarea>
            </v-expand-transition>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              :disabled="isSubmittingQuickFeedback"
              class="cancel-btn"
              @click="resetFeedback"
            >
              {{ $t('feedback.cancelQuickFeedbackBtn') }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="isSubmittingQuickFeedback"
              :disabled="!formValid"
              class="save-btn"
              text
              @click="saveFeedback"
            >
              {{ $t('feedback.saveQuickFeedbackBtn') }}
            </v-btn>
          </v-card-actions>
          <ErrorAlert
            v-if="hasErrors"
            type="error"
            :error="error"
            class="ma-0"
          />
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@styles';
</style>
